/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { css } from "@emotion/css";
import { Level2OuterPageLayout, Level2OuterPageLoading, StatusBadge } from "@octopusdeploy/design-system-components";
import type { PageSideNavLink } from "@octopusdeploy/design-system-components";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { MachineModelHealthStatus, MachineResource } from "@octopusdeploy/octopus-server-client";
import { isStepPackageEndpointResource, Permission, EndpointsHelper } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { compact } from "lodash";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AnyAction } from "redux";
import type { ThunkDispatch } from "redux-thunk";
import { MachineHealthStatusIcon } from "~/areas/infrastructure/components/MachineHealthStatusIcons/MachineHealthStatusIcon";
import endpointRegistry from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import { repository } from "~/clientInstance";
import Chip from "~/components/Chips/Chip";
import DataBaseComponent from "~/components/DataBaseComponent";
import { createErrorInfoFromErrors } from "~/components/PageContent/createErrorInfo";
import type { UnknownStepPackageDeploymentTarget } from "~/components/StepPackageDeploymentTargetEditor/StepPackageDeploymentTarget";
import { useCreatePageSideNavLink } from "~/hooks/useCreatePageSideNavLink";
import type { CreatePageSideNavLink } from "~/hooks/useCreatePageSideNavLink";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { getMachineIconUrl } from "~/utils/MachineIconUrlFetchers/machineIconUrlFetchers";
import { machineActions } from "../../reducers/machines";
import { getCurrentMachine } from "../../reducers/selectors";
import { machineHealthStatusToStatusBadgeStatus } from "../MachineHealthStatusIcons/machineHealthStatusToStatusBadgeStatus";
interface MachineLayoutInternalProps {
    machineId: string;
    machineName?: string;
    machineType?: string;
    machineIconUrl?: string;
    machineIsDisabled?: boolean;
    isStepPackage?: boolean;
    machineHealthStatus?: MachineModelHealthStatus;
    title: string;
    getMachine(id: string): Promise<MachineResource>;
    isDeploymentTargetPage: boolean;
    onFetchMachine(machine: MachineResource): void;
    createPageSideNavLink: CreatePageSideNavLink;
    children?: React.ReactNode;
    isNewStatusIconsFeatureEnabled?: boolean;
}
interface MachineLayoutInternalState {
    stepPackageDeploymentTarget: UnknownStepPackageDeploymentTarget | undefined;
    machine: MachineResource | undefined;
}
class MachineLayoutInternal extends DataBaseComponent<MachineLayoutInternalProps, MachineLayoutInternalState> {
    constructor(props: MachineLayoutInternalProps) {
        super(props);
        this.state = {
            stepPackageDeploymentTarget: undefined,
            machine: undefined,
        };
    }
    componentDidMount() {
        if (this.props.machineId) {
            this.doBusyTask(async () => {
                const machine = await this.props.getMachine(this.props.machineId);
                const deploymentTarget = isStepPackageEndpointResource(machine.Endpoint) ? await endpointRegistry.getStepPackageDeploymentTarget(machine.Endpoint.DeploymentTargetTypeId, machine.Endpoint.StepPackageVersion) : undefined;
                this.setState((prevState) => ({ ...prevState, machine, stepPackageDeploymentTarget: deploymentTarget }));
                this.props.onFetchMachine(machine);
            });
        }
    }
    render() {
        const machineId = this.props.machineId;
        const navLinks: Array<PageSideNavLink | null> = [];
        if (machineId) {
            const settingsLinkHref = this.props.isDeploymentTargetPage
                ? links.deploymentTargetSettingsPage.generateUrl({ spaceId: this.state.machine?.SpaceId ?? repository.spaceId!, machineId })
                : links.workerSettingsPage.generateUrl({ spaceId: this.state.machine?.SpaceId ?? repository.spaceId!, machineId });
            const connectionLinkHref = this.props.isDeploymentTargetPage
                ? links.deploymentTargetConnectivityPage.generateUrl({ spaceId: this.state.machine?.SpaceId ?? repository.spaceId!, machineId })
                : links.workerConnectivityPage.generateUrl({ spaceId: this.state.machine?.SpaceId ?? repository.spaceId!, machineId });
            const eventLinksHref = this.props.isDeploymentTargetPage
                ? links.deploymentTargetEventsPage.generateUrl({ spaceId: this.state.machine?.SpaceId ?? repository.spaceId!, machineId })
                : links.workerEventsPage.generateUrl({ spaceId: this.state.machine?.SpaceId ?? repository.spaceId!, machineId });
            navLinks.push(this.props.createPageSideNavLink("Settings", settingsLinkHref));
            navLinks.push(this.props.createPageSideNavLink(<div className={styles.connectivityContainer}>
                        <span>Connectivity</span>
                        {this.props.machineHealthStatus &&
                    (this.props.isNewStatusIconsFeatureEnabled ? (<StatusBadge status={machineHealthStatusToStatusBadgeStatus(this.props.machineHealthStatus)} size="small"/>) : (<MachineHealthStatusIcon healthStatus={this.props.machineHealthStatus}/>))}
                    </div>, connectionLinkHref));
            if (this.props.isDeploymentTargetPage) {
                navLinks.push(this.props.createPageSideNavLink("Deployments", links.deploymentTargetDeploymentsPage.generateUrl({ spaceId: this.state.machine?.SpaceId ?? repository.spaceId!, machineId }), undefined, {
                    permission: Permission.TaskView,
                    wildcard: true,
                }));
                navLinks.push(this.props.createPageSideNavLink("Runbook Runs", links.deploymentTargetRunbookRunsPage.generateUrl({ spaceId: this.state.machine?.SpaceId ?? repository.spaceId!, machineId }), undefined, {
                    permission: Permission.TaskView,
                    wildcard: true,
                }));
            }
            navLinks.push(this.props.createPageSideNavLink("Events", eventLinksHref, undefined, { permission: Permission.EventView, wildcard: true }));
        }
        const rootLink = this.props.isDeploymentTargetPage
            ? links.deploymentTargetsPage.generateUrl({ spaceId: this.state.machine?.SpaceId ?? repository.spaceId! })
            : links.workersPage.generateUrl({ spaceId: this.state.machine?.SpaceId ?? repository.spaceId! });
        let machineType = this.props.machineType;
        if (this.props.isStepPackage) {
            machineType = this.state.stepPackageDeploymentTarget?.name ?? "";
        }
        if (!this.props.machineName) {
            const errorInfo = this.errors ? createErrorInfoFromErrors(this.errors) : undefined;
            return (<main>
                    <Level2OuterPageLoading errors={errorInfo}/>
                </main>);
        }
        const machineChips = [];
        if (machineType) {
            machineChips.push(<MachineChip label={machineType}/>);
        }
        if (this.props.machineIsDisabled) {
            machineChips.push(<MachineChip label={"Disabled"}/>);
        }
        return (<main>
                <Level2OuterPageLayout navItems={compact(navLinks)} header={{
                breadcrumbs: [{ label: this.props.title, pageUrl: rootLink }],
                title: this.props.machineName ?? this.props.title,
                logo: this.props.machineIconUrl ? { href: this.props.machineIconUrl, accessibleName: `${this.props.machineName} icon` } : undefined,
                titleChips: machineChips,
            }}>
                    {this.props.children}
                </Level2OuterPageLayout>
            </main>);
    }
    static displayName = "MachineLayoutInternal";
}
function MachineChip({ label }: {
    label: string;
}) {
    return (<Chip noMargin={true} noTooltip={true} backgroundColor={themeTokens.color.chip.filled.background.primary} labelColor={themeTokens.color.chip.filled.text.primary}>
            {label}
        </Chip>);
}
interface SharedMachineLayoutProps {
    title: string;
    machineId: string;
    getMachine(id: string): Promise<MachineResource>;
    isDeploymentTargetPage: boolean;
    children: React.ReactNode;
}
function SharedMachineLayout({ title, machineId, getMachine, isDeploymentTargetPage, children }: SharedMachineLayoutProps) {
    const machine = useSelector(getCurrentMachine);
    const dispatchFetchMachine = useDispatchFetchMachine();
    const createPageSideNavLink = useCreatePageSideNavLink();
    const isNewStatusIconsFeatureEnabled = useOctopusFeatureToggle("new-status-icons", false);
    return (<MachineLayoutInternal machineId={machineId} machineName={machine?.name} machineType={machine?.machineType} machineIconUrl={machine?.machineIconUrl} machineIsDisabled={machine?.isDisabled} title={title} getMachine={getMachine} isDeploymentTargetPage={isDeploymentTargetPage} machineHealthStatus={machine?.healthStatus} onFetchMachine={dispatchFetchMachine} createPageSideNavLink={createPageSideNavLink} isNewStatusIconsFeatureEnabled={isNewStatusIconsFeatureEnabled}>
            {children}
        </MachineLayoutInternal>);
}
export function WorkerMachineLayout({ machineId, children }: {
    machineId: string;
    children: React.ReactNode;
}) {
    return (<SharedMachineLayout title="Workers" machineId={machineId} getMachine={(id: string) => repository.Workers.get(id)} isDeploymentTargetPage={false}>
            {children}
        </SharedMachineLayout>);
}
export function MachineLayout({ machineId, children }: {
    machineId: string;
    children: React.ReactNode;
}) {
    return (<SharedMachineLayout title="Deployment Targets" machineId={machineId} getMachine={(id: string) => repository.Machines.get(id)} isDeploymentTargetPage={true}>
            {children}
        </SharedMachineLayout>);
}
const useDispatchFetchMachine = () => {
    const dispatch: ThunkDispatch<GlobalState, void, AnyAction> = useDispatch();
    return React.useCallback((machine: MachineResource) => {
        const machineIconUrl = getMachineIconUrl(machine);
        dispatch(machineActions.machineFetched({
            id: machine.Id,
            name: machine.Name,
            machineIconUrl,
            machineType: EndpointsHelper.getFriendlyName(machine),
            isDisabled: machine.IsDisabled,
            isStepPackage: isStepPackageEndpointResource(machine.Endpoint),
            healthStatus: machine.HealthStatus,
        }));
    }, [dispatch]);
};
const styles = {
    connectivityContainer: css({
        display: "flex",
        alignItems: "center",
        gap: space[6],
    }),
};
